/* Lyric Artifact — archival-styled card that lives on the album page.
 * Only renders when the album has one or more booklet pages configured
 * via the admin form. Clicking "Enter Reading Room" summons the
 * ReadingRoom fullscreen experience (see ./ReadingRoom.js).
 */
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { StarMark } from "@/components/neoverse/Glyphs";
import { ReadingRoom } from "./ReadingRoom";

const HIDE_STATUSES = new Set([
  "sealed",
  "sealed — not yet cataloged",
  "sealed - not yet cataloged",
  "not available",
  "unavailable",
  "restricted",
]);

function isVisible(album) {
  const pages = Array.isArray(album?.booklet_pages) ? album.booklet_pages : [];
  if (pages.length === 0) return false;
  const status = (album.booklet_status || "").trim().toLowerCase();
  if (HIDE_STATUSES.has(status)) return false;
  return true;
}

export function LyricArtifact({ album }) {
  const [open, setOpen] = useState(false);
  if (!isVisible(album)) return null;

  const label = album.booklet_label || "Original Booklet";
  const condition = album.booklet_condition || "Excellent";
  const status = album.booklet_status || "Available for Consultation";
  const pages = album.booklet_pages;

  return (
    <>
      <section
        data-testid="lyric-artifact-card"
        className="mt-14 rounded-2xl border border-border/70 bg-[hsl(var(--card))] p-6 sm:p-8 relative overflow-hidden"
      >
        <div className="flex items-center gap-3 mb-4 text-[rgba(199,168,106,0.9)]">
          <StarMark size={11} />
          <p className="font-mono tracking-archival text-[10.5px] text-[rgba(199,194,184,0.65)]">
            Lyric Artifact
          </p>
        </div>

        <dl className="space-y-2 mb-8">
          <ArtifactRow term="" description={label} large />
          <ArtifactRow term="Condition" description={condition} />
          <ArtifactRow term="Archive Status" description={status} />
        </dl>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Button
            onClick={() => setOpen(true)}
            data-testid="enter-reading-room-btn"
            className="bg-[rgba(199,168,106,0.95)] text-[#0B0C0F] hover:bg-[rgba(199,168,106,1)] font-mono tracking-archival text-[11px] px-5"
          >
            Enter Reading Room
          </Button>
          <p className="font-mono tracking-archival text-[10px] text-[rgba(199,194,184,0.5)]">
            {pages.length} page{pages.length === 1 ? "" : "s"} · read-only
          </p>
        </div>
      </section>

      <ReadingRoom
        open={open}
        onClose={() => setOpen(false)}
        pages={pages}
        albumTitle={album.title}
      />
    </>
  );
}

function ArtifactRow({ term, description, large }) {
  return (
    <div className="grid grid-cols-[140px,1fr] gap-4 items-baseline">
      {term ? (
        <dt className="font-mono tracking-archival text-[10px] text-[rgba(199,194,184,0.55)] uppercase">
          {term}
        </dt>
      ) : (
        <dt aria-hidden="true" />
      )}
      <dd
        className={
          large
            ? "font-serif text-2xl text-[rgba(231,224,214,0.95)]"
            : "font-serif italic text-[rgba(231,224,214,0.82)]"
        }
      >
        {description}
      </dd>
    </div>
  );
}
